import React, { useState, useEffect, useRef } from 'react'
import { GoogleMap, LoadScript, TrafficLayer, InfoWindow, useGoogleMap } from '@react-google-maps/api';
import useScreenOrientation from 'react-hook-screen-orientation'
import EndpointLayer from './EndpointLayer';
import { getFeatureInfo } from './WmsGetFeatureInfo';
import { AdtInfoWindow } from '../modules/ADT/index';
import Box from "@mui/material/Box";
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { LayersControl, LayersButton, LayersControlButtons } from './MapLayerControl';
import { DirectionsButton, DirectionsControl } from './MapDirectionsControl';
import DomainContext from '../context/DomainContext';
import TasMarquee from './TasMarquee'
import LayersContext from '../context/LayersContext';
import getMapType from './WmsMapType'
import _ from 'lodash'
import { setDomain, publishLayer } from '../util/ArrayUtil';
import PopupInfoWindow from './PopupInfoWindow';
import PlacesSearchBox from './PlacesSearchBox';
import CustomAlert from './CustomAlert';
import GeoJsonLayer from './GeoJsonLayer';
import { Polyline } from '@react-google-maps/api';
import CongestionRegionSliderControl from './CongestionRegionsSliderControl';
import { CongestionLegend, HistoricalCongestionLegend } from './CongestionLegend';

const containerStyle = {
    height: "100%",
    width: "100%"
};

const MapControl = (props) => {
    const map = useGoogleMap();
    const ref = useRef();
    const position = props.position;

    useEffect(() => {
        if (map && ref) {
            map.controls[window.google.maps.ControlPosition[position]].push(
                ref.current
            );
        }
    }, [map, ref, position])
    return <span ref={ref}>{props.children}</span>;
}

const libraries = ["places"];

var routeLookupObj = {
    "origin": {
        "crossStreet": ""
    },
    "destination": {
        "crossStreet": ""
    },
    "vehicle": { externalId: "" },
    "originAddress": { error: false, addressNum: "", roadwayName: null, marker: null },
    "destinationAddress": { error: false, addressNum: "", roadwayName: null, marker: null },
    "originDirection":null,
    "destinationDirection":null,
    "hasPermit": false
}

const routeColors = ["#3399FF", "#33FF00"]

const getRouteStyle = (map, type, index) => {
    const defaultOptions = {
        clickable: true,
        draggable: false,
        editable: false,
        visible: true,
    }
    let zoom = map.getZoom();
    var width = 10;
    if (zoom < 12) {
        width = 6;
    }

    if (type === "outline") {
        return (
            {
                ...defaultOptions,
                strokeColor: '#34495E',
                strokeOpacity: 0.8,
                strokeWeight: width
            }
        )

    } else {
        return (
            {
                ...defaultOptions,
                strokeColor: routeColors[index],
                strokeOpacity: 0.6,
                strokeWeight: width / 2
            }
        )

    }
}

function Map(props) {
    const layersContext = React.useContext(LayersContext);
    const [layers, setLayers] = React.useState([]);
    const [styles, setStyles] = React.useState([]);
    const [layerButtonOpen, setLayerButtonOpen] = React.useState(false);
    const [directionsButtonOpen, setDirectionsButtonOpen] = React.useState(false);
    const [map, setMap] = React.useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [apiKey, setApiKey] = React.useState(null);
    const [trafficLayer, setTrafficLayer] = React.useState(false);
    const [showWMS, setShowWMS] = React.useState(true);
    const screenOrientation = useScreenOrientation();
    const [center, setCenter] = useState(screenOrientation.includes("landscape") ? { lat: 41.83730086220008, lng: -87.66876030777101 } : { lat: 41.848527243750254, lng: -87.67578023476486 });
    const [refreshWms, setRefreshWms] = React.useState(false);
    const zoom = screenOrientation.includes("landscape") ? 11 : 10;
    const modules = props.modules;
    const domains = React.useContext(DomainContext);
    const loggedIn = props.loggedIn;
    const setLayersOn = props.setLayersOn;
    const footerSelection = props.footerSelection;
    const setFooterSelection = props.setFooterSelection;
    const [showAlert, setShowAlert] = React.useState(false);
    const [message, setMessage] = React.useState("")
    const [severity, setSeverity] = React.useState("success");
    const [currentZoom, setCurrentZoom] = React.useState(zoom);
    const mobileOpen = props.mobileOpen;
    const [options, setOptions] = React.useState(null);
    const [clicklatlng, setClickLatLng] = React.useState(null);
    const [routes, setRoutes] = React.useState([]);
    const [selectedRoutes, setSelectedRoutes] = React.useState([]);
    const [routeLookup, updateRouteLookup] = React.useState(routeLookupObj)
    const [congestionRegionsOn, setCongestionRegionsOn] = React.useState(false);
    const [wmsPopup, setWmsPopup] = React.useState(null);

    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('md'));
    const isLandscape = screenOrientation.includes("landscape");

    useEffect(() => {
        if(directionsButtonOpen) {
            setLayerButtonOpen(false);
        }
    },[directionsButtonOpen])

    useEffect(() => {
        if (undefined !== layersContext['Google Traffic']) {
            setTrafficLayer(layersContext['Google Traffic'].enabled);
        }
    }, [layersContext])

    useEffect(() => {
        if (footerSelection && footerSelection === 2) {
            const getLocation = () => {
                if (!navigator.geolocation) {
                    setMessage('Geolocation is not supported by your browser');
                    setSeverity("warning");
                    setFooterSelection(null);
                } else {
                    setMessage('Locating...');
                    setSeverity("success");
                    navigator.geolocation.getCurrentPosition((position) => {
                        setCenter({ lat: position.coords.latitude, lng: position.coords.longitude })
                    }, () => {
                        setMessage('Unable to retrieve your location');
                        setSeverity("warning");
                        setFooterSelection(null);
                    });
                }

            }
            getLocation();
        }
    }, [footerSelection, setFooterSelection])

    useEffect(() => {
        const fetchApi = async () => {
            const res = await fetch('static/mapstyle.json', {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            res.json()
                .then(res => {
                    setOptions({
                        styles: res
                    })
                })
        }
        fetchApi().catch(err => {
            console.log(err);
        });
        setInterval(() => {
            setRefreshWms(true);
        }, 180000)
    }, []);

    const resetWmsLayer = (map, layers, styles) => {
        if (null != map && layers.length > 0) {
            var wmsLayer = new window.google.maps.ImageMapType(getMapType(process.env.REACT_APP_GEOSERVER_HOST, layers.join(","), styles.join(",")));
            for (let i = 0; i < map.overlayMapTypes.length; i++) {
                var eLayer = map.overlayMapTypes.getAt(i);
                if (eLayer.name === wmsLayer.name) {
                    map.overlayMapTypes.removeAt(i);
                    break;
                }
            }
            map.overlayMapTypes.push(wmsLayer)
        }
        if (map && layers.length === 0) {
            for (let i = 0; i < map.overlayMapTypes.length; i++) {
                map.overlayMapTypes.removeAt(i);
            }
        }
    }

    useEffect(() => {
        if (refreshWms) {
            resetWmsLayer(map, layers, styles);
        }
        setRefreshWms(false);
    }, [refreshWms, map, layers])

    useEffect(() => {
        var wmsLayers = [];
        var wmsStyles = [];
        if (showWMS) {
            modules.map(module => {
                if (module.wmsMapLayer && undefined != layersContext[module.name] && layersContext[module.name].enabled && publishLayer(module.wmsPublish, domains)) {
                    wmsLayers.push(module.wmsMapLayer)
                    if (module.wmsMapStyle) {
                        wmsStyles.push(module.wmsMapStyle)
                    }
                }
            })
        }
        if (!_.isEqual(wmsLayers, layers)) {
            setLayers(wmsLayers);
        }
        if (!_.isEqual(wmsStyles, styles)) {
            setStyles(wmsStyles);
        }
        if (layersContext && layersContext['Regional Congestion']?.enabled) {
            setCongestionRegionsOn(true)
        } else {
            setCongestionRegionsOn(false)
        }
    }, [layersContext, showWMS])

    useEffect(() => {
        resetWmsLayer(map, layers, styles);
    }, [map, layers, styles])

    useEffect(() => {
        setDomain(domains, "tasWebapp.map.googleApiKey", setApiKey);
        setDomain(domains, "tasWebapp.wmsLayers.enable", setShowWMS);
    }, [domains])

    useEffect(() => {
        if (null != map) {
            map.addListener("zoom_changed", () => {
                setCurrentZoom(map.getZoom());
            })
        }
    }, [map])

    const onCloseClick = () => {
        setLayerButtonOpen(layerButtonOpen => !layerButtonOpen)
    }

    const onLoad = (gmap, matches) => {
        let mapOptions = {
            mapTypeControl: true,
            mapTypeControlOptions: {
                style: window.google.maps.MapTypeControlStyle.DEFAULT,
                position: !matches ? window.google.maps.ControlPosition.RIGHT_BOTTOM : window.google.maps.ControlPosition.BOTTOM_CENTER,
            },
            zoomControl: true,
            zoomControlOptions: {
                position: window.google.maps.ControlPosition.RIGHT_TOP,
            },
            streetViewControlOptions: {
                position: window.google.maps.ControlPosition.RIGHT_TOP
            },
            scaleControl: false,
            streetViewControl: true,
            fullscreenControl: false,
            clickableIcons: false,
            minZoom: matches ? 11 : isLandscape ? 9 : 10,
            maxZoom: 17
        }
        setOptions({ ...options, ...mapOptions });
        gmap.setOptions(options);
        gmap.addListener("dragend", (e) => {
            setCenter(gmap.getCenter());
        });
        setMap(gmap);
    }

    const handleOnClick = async (e, directionsButtonOpen) => {
        if (directionsButtonOpen) {
            setClickLatLng(e.latLng);
            return;
        }
        setClickLatLng(null);

        // Check for clickable WMS layers (ADT)
        if (map && layersContext['Average Daily Traffic Counts']?.enabled) {
            console.log("[ADT Click] Lat:", e.latLng.lat(), "Lng:", e.latLng.lng(), "Zoom:", map.getZoom());
            const props = await getFeatureInfo(
                map,
                e.latLng,
                'cdot:congestionadt',
                process.env.REACT_APP_GEOSERVER_HOST
            );
            console.log("[ADT Click] GetFeatureInfo result:", props);
            if (props) {
                setWmsPopup({ position: e.latLng, feature: props });
                setSelectedMarker(null);
            } else {
                setWmsPopup(null);
            }
        } else {
            setWmsPopup(null);
        }
    }

    const clearRouteLookup = () => {
        updateRouteLookup(routeLookup => {
            if (null !== routeLookup.originAddress.marker) {
                routeLookup.originAddress.marker.setMap(null);
            }
            if (null !== routeLookup.destinationAddress.marker) {
                routeLookup.destinationAddress.marker.setMap(null);
            }
            return _.cloneDeep(routeLookupObj)
        })
    }

    const loadmap = (
        <>
            <Box component="div" sx={{ flexGrow: 0 }}>
                <TasMarquee {...props} />
            </Box>
            <Box component="div" sx={{ flexGrow: 1 }}>
                <LoadScript
                    googleMapsApiKey={apiKey}
                    libraries={libraries}
                >
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={center}
                        zoom={zoom}
                        options={options}
                        onLoad={(map) => onLoad(map, matches)}
                        onClick={(e) => { handleOnClick(e, directionsButtonOpen) }}
                    >
                        {trafficLayer ? <TrafficLayer /> : null}
                        {modules.map(module => {
                            if (module.showOnMap && module.endpoint) {
                                return (
                                    <EndpointLayer setLayersOn={setLayersOn} module={module} key={module.name} selectedMarker={selectedMarker} setSelectedMarker={setSelectedMarker} setRefreshWms={setRefreshWms} currentZoom={currentZoom} />
                                )
                            }
                        })}
                        {modules.map(module => {
                            if (module.showOnMap && module.geoJsonUrl && publishLayer(module.publishLayer, domains)) {
                                return (
                                    <GeoJsonLayer module={module} setLayersOn={setLayersOn} key={module.name} />
                                )
                            }
                        })}
                        {modules.map(module => {
                            if (module.showOnMap && module.staticLayer && publishLayer(module.publishLayer, domains)) {
                                const StaticLayer = module.staticLayer;
                                return (
                                    <StaticLayer module={module} key={module.name + "-static"} setSelectedMarker={setSelectedMarker} />
                                )
                            }
                        })}
                        <>
                            
                            <MapControl position={"RIGHT_BOTTOM"}>
                                <LayersButton {...props} layerButtonOpen={layerButtonOpen} setLayerButtonOpen={setLayerButtonOpen} />
                            </MapControl>
                            <MapControl position="RIGHT_BOTTOM">{layerButtonOpen && matches ?
                                <LayersControl {...props} showWMS={showWMS} domains={domains} /> : null}
                                {congestionRegionsOn ? <CongestionRegionSliderControl {...props} setCongestionRegionsOn={setCongestionRegionsOn}/> : null}
                            </MapControl>
                            
                                <MapControl position="TOP_LEFT">
                                    <PlacesSearchBox map={map} />
                                    
                                    <DirectionsButton 
                                        setDirectionsButtonOpen={setDirectionsButtonOpen}
                                        loggedIn={loggedIn}
                                        setShowAlert={setShowAlert}
                                        setMessage={setMessage}
                                        setSeverity={setSeverity}
                                    />
                                    
                                    {loggedIn && directionsButtonOpen ? 
                                        <DirectionsControl {...props} clicklatlng={clicklatlng} setClickLatLng={setClickLatLng} clearRouteLookup={clearRouteLookup} routes={routes}
                                            setRoutes={setRoutes} setSelectedRoutes={setSelectedRoutes} selectedRoutes={selectedRoutes} updateRouteLookup={updateRouteLookup} routeLookup={routeLookup} />
                                     : null}
                                    
                                </MapControl>
                            
                            {selectedMarker &&
                                <PopupInfoWindow selectedMarker={selectedMarker} setSelectedMarker={setSelectedMarker} />
                            }
                            {wmsPopup &&
                                <InfoWindow
                                    position={wmsPopup.position}
                                    onCloseClick={() => setWmsPopup(null)}
                                >
                                    <AdtInfoWindow feature={wmsPopup.feature} />
                                </InfoWindow>
                            }
                            <MapControl position="LEFT_BOTTOM">
                                {layersContext && layersContext['Congestion']?.enabled && !layersContext['Historical Congestion']?.enabled &&
                                    <CongestionLegend />
                                }
                                {layersContext && layersContext['Historical Congestion']?.enabled &&
                                    <HistoricalCongestionLegend />
                                }
                            </MapControl>

                        </>
                        {routes && routes.pathPolyLines && routes.pathPolyLines.length > 0 &&
                            routes.pathPolyLines.map((route, index) => {
                                if (selectedRoutes[index]) {
                                    return (
                                        <PolylineLayer key={index} map={map} route={route} index={index} />
                                    )
                                } else {
                                    return null;
                                }
                            })}
                    </GoogleMap>
                </LoadScript>
            </Box>
        </>
    )

    //below handles changes between phone and larger screen surfaces
    //layer selector pops open on large screen surfaces
    //slides up on small
    //      width: "calc(100vw - 350px)",
    // className={isDark ? "dark map" : "light map"}
    if (matches) {
        return (
            <>
                <CustomAlert showAlert={showAlert} message={message} severity={severity} setShowAlert={setShowAlert} setMessage={setMessage} />
                <Box component="div" sx={{
                    flexGrow: "2",
                    display: { xs: 'none', sm: 'none', md: 'flex' },
                    flexDirection: "column"
                }} className={mobileOpen ? "map map-drawer" : "map map-no-drawer"} >
                    {apiKey && options && loadmap}
                </Box>
            </>

        )
    } else {
        return (
            <>
                <CustomAlert showAlert={showAlert} message={message} severity={severity} setShowAlert={setShowAlert} setMessage={setMessage} />
                <Box component="div" sx={{
                    flexGrow: "2",
                    width: "100%",
                    display: { xs: 'flex', sm: 'flex', md: 'none' },
                    flexDirection: "column"
                }} className="map">
                    {apiKey && options && loadmap}
                </Box>
                <Box sx={{ display: { xs: 'block', sm: 'block', md: 'none' }, width: "100%", position: "absolute", bottom: 65, right: 0, left: 0, maxHeight:"50%", overflow:"scroll" }}>
                    <Box component="div" sx={{
                        backgroundColor: "rgba(171, 183, 183,.8)",
                        zIndex: "1000"
                    }}>
                        <Slide direction='up' in={layerButtonOpen} mountOnEnter unmountOnExit>
                            <div>
                                <Box sx={{ display: "flex", color: "black" }}>
                                    <Typography
                                        component="div"
                                        variant="subtitle2"
                                        color="black"
                                        sx={{ flexGrow: 1, lineHeight: "30px", marginLeft: "5px" }}
                                    >
                                        Select Layers
                                    </Typography>
                                    <Box sx={{ flexGrow: 1, color: "black", textAlign: "right" }}>
                                        <IconButton aria-label="close" size="small" sx={{ color: "black" }} onClick={onCloseClick}>
                                            <CloseIcon />
                                        </IconButton>
                                    </Box>

                                </Box>
                                <LayersControlButtons {...props} showWMS={showWMS} domains={domains} />
                            </div>
                        </Slide></Box>
                </Box>
            </>
        )
    }
}

const PolylineLayer = (props) => {
    const route = props.route;
    const map = props.map;
    const index = props.index;
    return (
        <>
            <Polyline path={route.routePolyline} options={getRouteStyle(map, "outline", index)} key={"outline"} />
            <Polyline path={route.routePolyline} options={getRouteStyle(map, "primary", index)} key={"primary"} />
        </>
    )
}

export default Map